// ResetPasswordPage.jsx
// Handles the password reset link from Supabase email
// URL: /reset-password?code=xxxxx  (Supabase sends a PKCE code)

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function ResetPasswordPage() {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();

  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState(false);
  const [ready,     setReady]     = useState(false); // session exchanged

  // Exchange the code in the URL for a session so updateUser works
  useEffect(() => {
    const code = searchParams.get('code');
    if (!code) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }
    supabase.auth.exchangeCodeForSession(code)
      .then(({ error }) => {
        if (error) setError('This reset link has expired. Please request a new one.');
        else setReady(true);
      });
  }, []);

  const handleSubmit = async () => {
    setError('');
    if (!password) { setError('Please enter a new password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) { setError(error.message); return; }
    setSuccess(true);
    setTimeout(() => navigate('/app'), 2500);
  };

  const inputStyle = {
    width: '100%', background: 'var(--bg)', color: 'var(--text)',
    border: '0.5px solid var(--border2)', borderRadius: 10,
    padding: '12px 14px', fontFamily: 'var(--font)', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)', padding: 24,
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: 'var(--surface)',
        border: '0.5px solid var(--border2)',
        borderRadius: 20, padding: '36px 32px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.03em' }}>
            CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>✓</div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', margin: '0 0 10px' }}>
              Password updated!
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
              Redirecting you to the app…
            </p>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
              Set new password
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 24px', lineHeight: 1.6 }}>
              Choose a strong password for your account.
            </p>

            {error && (
              <div style={{
                fontSize: 13, color: '#e24b4a',
                background: 'rgba(226,75,74,0.08)',
                border: '0.5px solid rgba(226,75,74,0.25)',
                borderRadius: 8, padding: '10px 14px', marginBottom: 18, lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            {!error.includes('expired') && !error.includes('Invalid') && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, color: 'var(--dim)', display: 'block', marginBottom: 6 }}>
                    New password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    placeholder="At least 6 characters"
                    style={inputStyle}
                    autoFocus
                    disabled={!ready}
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 12, color: 'var(--dim)', display: 'block', marginBottom: 6 }}>
                    Confirm password
                  </label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    placeholder="Repeat your password"
                    style={inputStyle}
                    disabled={!ready}
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading || !ready}
                  style={{
                    width: '100%', background: '#7f77dd', color: '#fff', border: 'none',
                    borderRadius: 10, padding: '13px',
                    fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600,
                    cursor: (loading || !ready) ? 'wait' : 'pointer',
                    opacity: (loading || !ready) ? 0.7 : 1,
                    boxShadow: '0 4px 14px rgba(127,119,221,0.35)',
                    transition: 'all 0.15s',
                  }}
                >
                  {loading ? 'Saving…' : 'Set new password'}
                </button>
              </>
            )}

            {(error.includes('expired') || error.includes('Invalid')) && (
              <button
                onClick={() => navigate('/auth')}
                style={{
                  width: '100%', background: 'none', color: '#7f77dd',
                  border: '0.5px solid rgba(127,119,221,0.4)',
                  borderRadius: 10, padding: '13px', marginTop: 8,
                  fontFamily: 'var(--font)', fontSize: 14, cursor: 'pointer',
                }}
              >
                Back to sign in →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
